<template>
  <v-dialog
    v-model="dialog"
    scrollable
    width="400"
  >
    <v-card>
      <v-card-title>Team with Access to this {{ humanizedResourceType }}</v-card-title>
      <v-card-text>
        <v-list dense>
          <v-subheader>Teams</v-subheader>
          <v-list-item
            v-for="(team, i) of teams"
            :key="i"
          >
            <v-list-item-content>
              <v-list-item-title>{{ team.name }}</v-list-item-title>
            </v-list-item-content>

            <v-list-item-action>
              <v-tooltip right>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-if="userCanExcludeTeam"
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="excludeTeam(team)"
                  >
                    <v-icon
                      color="red"
                    >
                      mdi-eye-remove
                    </v-icon>
                  </v-btn>
                </template>
                <span>Remove Team's Access</span>
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions>
        <v-btn
          color="primary"
          small
          outlined
          :loading="loading"
          @click="_ => onAssociateToTeam()"
        >
          + Associate Team
        </v-btn>
        <v-spacer />
        <v-btn
          text
          color="secondary"
          @click="dialog = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
    <ConfirmActionDialog
      v-model="showConfirmDialog"
      :title="confirmDialogTitle"
      :message="confirmDialogMessage"
      @confirm="_ => onConfirmExcludeRecord()"
    />
    <SelectTeamDialog
      v-model="showSelectTeamDialog"
      :loading="loading"
      @selected="teamId => onSelectedTeam(teamId)"
    />
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { humanize, parseErrorResponseMessage, singularize } from '@/utilities'
import actions from '@/data/Permissions/enums.json'
import { alertHandler } from '@/alertHandler'

import { TEAM_NAMESPACE } from '@/store/team'
import { USER_NAMESPACE } from '@/store/user'

import ConfirmActionDialog from '@/components/Dialogs/Common/ConfirmActionDialog.vue'
import SelectTeamDialog from '@/components/Dialogs/Team/SelectTeamDialog.vue'

export default {
  name: 'ResourceTeamsDialog',

  components: {
    ConfirmActionDialog,
    SelectTeamDialog,
  },

  props: {

    resourceId: {
      type: [String, Number],
      default: null,
    },

    resourceType: {
      type: String,
      required: true,
    },

    teams: {
      type: Array,
      default: _ => [],
    },

    value: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      loading: false,
      selectedTeam: null,
      showConfirmDialog: false,
      showExcludeTeamDialog: false,
      showSelectTeamDialog: false,
    }
  },

  computed: {
    confirmDialogMessage () {
      return `Are you sure you want to exclude ${this.humanizedResourceType} from ${this.selectedTeam?.name}?`
    },

    confirmDialogTitle () {
      return `Exclude ${this.humanizedResourceType} from Team`
    },

    dialog: {
      get () {
        return this.value
      },

      set (value) {
        this.$emit('input', value)
      },
    },

    humanizedResourceType () {
      return singularize(this.humanize(this.resourceType))
    },

    userCan () {
      return {
        exclude: {
          legal_cases: this.can(actions.Team.LegalCases.Exclude),
          parties: this.can(actions.Team.Parties.Exclude),
          records: this.can(actions.Team.Records.Exclude),

        },
      }
    },

    userCanAssociateTeam () {
      // Reuse existing permission patterns; adjust if specific permission needed per resource type
      return this.can(actions.Team[this.humanizedResourceType.charAt(0).toUpperCase() + this.humanizedResourceType.slice(1) + 's']?.Permit || actions.Team.Records.Permit)
    },

    userCanExcludeTeam () {
      return this.userCan.exclude[this.resourceType]
    },

    ...mapGetters(USER_NAMESPACE, [
      'can',
    ]),
  },

  methods: {
    excludeTeam (team) {
      this.selectedTeam = team
      this.showConfirmDialog = true
    },

    humanize,

    onAssociateToTeam () {
      this.showSelectTeamDialog = true
    },

    onConfirmExcludeRecord () {
      this.excludeResourceFromTeam({
        resourceId: this.resourceId,
        resourceType: this.resourceType,
        teamId: this.selectedTeam.id,
      }).then(_ => {
        this.displayAlert('success', 'Record excluded successfully')
        this.$router.go(0)
      }).
        catch(e => this.displayAlert('error', parseErrorResponseMessage(e)))
    },

    onSelectedTeam (teamId) {
      this.loading = true
      this.associateResourceToTeam({
        resourceId: this.resourceId,
        resourceType: this.resourceType,
        teamId: teamId,
      }).
        then(_ => {
          this.showSelectTeamDialog = false
          this.displayAlert('success', 'Team associated successfully')
          this.$router.go(0)
        }).
        catch(e => this.displayAlert('error', parseErrorResponseMessage(e))).
        finally(_ => (this.loading = false))
    },

    ...mapActions(TEAM_NAMESPACE, [
      'associateResourceToTeam',
      'excludeResourceFromTeam',
    ]),
  },
}
</script>

<style scoped>
.team-icon {
  width: 10px;
  min-width: 10px;
}
</style>
